import { useTranslation } from "react-i18next"

import { AlertTriangle } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Spinner } from "@/components/ui/spinner"

export type ModelToDelete = {
  id: string
  displayName: string
}

type DeleteModelDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => Promise<void>
  isPending: boolean
  model: ModelToDelete | null
}

export function DeleteModelDialog({
  isOpen,
  onOpenChange,
  onConfirm,
  isPending,
  model,
}: DeleteModelDialogProps) {
  const { t } = useTranslation()

  if (!model) return null

  const handleConfirm = async () => {
    await onConfirm()
    onOpenChange(false)
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-destructive size-5" />
            {t("adminDashboard.models.deleteDialog.title", "Delete Model")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              "adminDashboard.models.deleteDialog.description",
              'Are you sure you want to delete the model "{{name}}"? This action cannot be undone.',
              { name: model.displayName }
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t("adminDashboard.models.deleteDialog.cancel", "Cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={isPending}
            onClick={(e) => {
              e.preventDefault()
              void handleConfirm()
            }}
          >
            {isPending ? (
              <>
                <Spinner />
                {t("adminDashboard.models.deleteDialog.deleting", "Deleting...")}
              </>
            ) : (
              t("adminDashboard.models.deleteDialog.delete", "Delete Model")
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
